import { useRef } from 'react'
import { message } from 'antd'
import { useLingui } from '@lingui/react/macro'

import { useAiMusicGeneratorStoreFromZustand } from '../../../_store/createStore'
import { AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH } from '../../../const'

import { buildMusicGeneratePayload } from './useFormGeneratePayload'

import type { MusicGeneratorFormValuesNew } from '../../../_types/form'

import { EFeatureName, useFeatureGuard } from '@/hooks/usageValidation'
import { api } from '@/utils/api'

export function useFormGenerate(options?: { onSuccess?: () => void }) {
  const { mutateAsync, isLoading: generateMusicLoading } =
    api.task.create.useMutation({
      onSuccess: () => {
        options?.onSuccess?.()
      },
    })
  const reloadTimeout = useRef(0)
  const guardGenerate = useFeatureGuard(EFeatureName.Generate)
  const { t } = useLingui()
  const lyrics = useAiMusicGeneratorStoreFromZustand(
    (state) => state.lyricsTextareaContent,
  )
  const customLyrics = useAiMusicGeneratorStoreFromZustand(
    (state) => state.lyricsCustomTextareaContent,
  )
  const finalTaskType = useAiMusicGeneratorStoreFromZustand(
    (state) => state.finalTaskType,
  )
  const finalImageUrl = useAiMusicGeneratorStoreFromZustand(
    (state) => state.finalImageUrl,
  )
  const finalPromptSelectMode = useAiMusicGeneratorStoreFromZustand(
    (state) => state.finalPromptSelectMode,
  )
  const finalPrompt = useAiMusicGeneratorStoreFromZustand(
    (state) => state.finalPrompt,
  )

  const handleGenerate = guardGenerate(
    async (values: MusicGeneratorFormValuesNew) => {
      //创建接口未返回或者1.5s内不重复请求
      if (
        generateMusicLoading ||
        (Date.now() - reloadTimeout.current) / 1000 <= 1.5
      ) {
        return
      }
      reloadTimeout.current = Date.now()
      const { promptTabsMode, styleMode, customTaskType } = values
      const { description } =
        promptTabsMode === 'smart'
          ? {
              description: values.recommendDescription || values.description,
            }
          : {
              description:
                values.SongrecommendDescription ||
                values.Songdescription ||
                values.Instrumentaldescription,
            }

      switch (promptTabsMode) {
        // 智能表单
        case 'smart': {
          // 快速模式
          if (finalPromptSelectMode === 'quick') {
            // 提示词和图片两者必须有一个
            if (!finalPrompt && !finalImageUrl) {
              message.error(t`Please enter prompt or upload image`)
              break
            }

            const quickPayload = buildMusicGeneratePayload({
              values,
              state: {
                finalTaskType,
                finalImageUrl,
                finalPromptSelectMode,
                finalPrompt,
                lyrics,
                customLyrics,
              },
            })
            if (!quickPayload) {
              throw new Error('Failed to build quick payload')
            }

            await mutateAsync(quickPayload)
          }
          // 智能模式
          if (finalPromptSelectMode === 'smart') {
            // 提示词和图片两者必须有一个
            if (!finalPrompt && !finalImageUrl) {
              message.error(t`Please enter prompt`)
              break
            }
            if (!description) {
              message.error(t`Please choose a style or input prompt`)
              break
            }

            if (finalTaskType === 'Song') {
              // 歌词长度超过最大长度
              if (
                lyrics &&
                lyrics.length > AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH
              ) {
                message.info(
                  t`Lyrics length exceeds the maximum length, please enter less than ${AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH} characters`,
                )
                return
              }

              // 没有歌词不能提交
              if (lyrics.trim().length === 0) {
                message.error(t`Please enter lyrics`)
                break
              }
            }

            const smartPayload = buildMusicGeneratePayload({
              values,
              state: {
                finalTaskType,
                finalImageUrl,
                finalPromptSelectMode,
                finalPrompt,
                lyrics,
                customLyrics,
              },
            })
            if (!smartPayload) {
              throw new Error('Failed to build smart payload')
            }

            await mutateAsync(smartPayload)
          }
          break
        }
        // 自定义表单
        case 'custom': {
          // 歌曲模式
          if (customTaskType === 'Song') {
            // 歌词长度超过最大长度
            if (
              customLyrics &&
              customLyrics.trim().length > AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH
            ) {
              message.info(
                t`Lyrics length exceeds the maximum length, please enter less than ${AI_MUSIC_GENERATOR_MAX_LYRICS_LENGTH} characters`,
              )
              return
            }

            // 没有歌词不能提交
            if (customLyrics.trim().length === 0) {
              message.error(t`Please enter lyrics`)
              break
            }

            if (styleMode === 'prompt' || styleMode === 'recommend') {
              if (!description) {
                message.error(t`Please choose a style or input prompt`)
                break
              }
              const customSongPromptPayload = buildMusicGeneratePayload({
                values,
                state: {
                  finalTaskType,
                  finalImageUrl,
                  finalPromptSelectMode,
                  finalPrompt,
                  lyrics,
                  customLyrics,
                },
              })
              if (!customSongPromptPayload) {
                throw new Error('Failed to build custom song prompt payload')
              }
              await mutateAsync(customSongPromptPayload)
            } else {
              if (
                !values.songReference?.token &&
                !values.vocalReference?.token
              ) {
                message.error(t`Please upload one reference`)
                break
              }
              const customSongReferencePayload = buildMusicGeneratePayload({
                values,
                state: {
                  finalTaskType,
                  finalImageUrl,
                  finalPromptSelectMode,
                  finalPrompt,
                  lyrics,
                  customLyrics,
                },
              })
              if (!customSongReferencePayload) {
                throw new Error('Failed to build custom song reference payload')
              }
              await mutateAsync(customSongReferencePayload)
            }
          }

          // 纯音乐模式
          if (customTaskType === 'Instrumental') {
            if (styleMode === 'prompt') {
              if (!description) {
                message.error(t`Please input prompt`)
                break
              }
              const customInstrumentalPromptPayload = buildMusicGeneratePayload(
                {
                  values,
                  state: {
                    finalTaskType,
                    finalImageUrl,
                    finalPromptSelectMode,
                    finalPrompt,
                    lyrics,
                    customLyrics,
                  },
                },
              )
              if (!customInstrumentalPromptPayload) {
                throw new Error(
                  'Failed to build custom instrumental prompt payload',
                )
              }
              await mutateAsync(customInstrumentalPromptPayload)
            } else {
              if (!values.instrumentalReference?.token) {
                message.error(t`Please upload instrument reference`)
                break
              }
              const customInstrumentalReferencePayload =
                buildMusicGeneratePayload({
                  values,
                  state: {
                    finalTaskType,
                    finalImageUrl,
                    finalPromptSelectMode,
                    finalPrompt,
                    lyrics,
                    customLyrics,
                  },
                })
              if (!customInstrumentalReferencePayload) {
                throw new Error(
                  'Failed to build custom instrumental reference payload',
                )
              }
              await mutateAsync(customInstrumentalReferencePayload)
            }
          }
          break
        }
        default: {
          throw new Error('Invalid promptTabsMode')
        }
      }
    },
  )

  return {
    handleGenerate,
    generateMusicLoading,
  }
}
